import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

const AudioPlayerContext = createContext(null);

export function AudioPlayerProvider({ children }) {
  const [track, setTrack] = useState(null); // { id, title, url, artist? }
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const animRef = useRef(null);

  const updateProgress = useCallback(() => {
    const a = audioRef.current;
    if (a && a.duration) {
      setCurrentTime(a.currentTime);
      setDuration(a.duration);
      setProgress((a.currentTime / a.duration) * 100);
    }
    animRef.current = requestAnimationFrame(updateProgress);
  }, []);

  const play = useCallback((id, title, url, artist) => {
    if (track && track.id === id && audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
        cancelAnimationFrame(animRef.current);
      } else {
        audioRef.current.play().then(() => {
          setPlaying(true);
          animRef.current = requestAnimationFrame(updateProgress);
        }).catch(() => {});
      }
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      cancelAnimationFrame(animRef.current);
    }
    const a = new Audio(url);
    audioRef.current = a;
    setTrack({ id, title, url, artist });
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    a.onended = () => { setPlaying(false); setProgress(100); cancelAnimationFrame(animRef.current); };
    a.onerror = () => { setPlaying(false); cancelAnimationFrame(animRef.current); };
    a.onloadedmetadata = () => setDuration(a.duration);
    a.play().then(() => {
      setPlaying(true);
      animRef.current = requestAnimationFrame(updateProgress);
    }).catch(() => setPlaying(false));
  }, [track, playing, updateProgress]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
    cancelAnimationFrame(animRef.current);
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && track) {
      audioRef.current.play().then(() => {
        setPlaying(true);
        animRef.current = requestAnimationFrame(updateProgress);
      }).catch(() => {});
    }
  }, [track, updateProgress]);

  const seek = useCallback((pct) => {
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = (pct / 100) * audioRef.current.duration;
      setProgress(pct);
    }
  }, []);

  const close = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    cancelAnimationFrame(animRef.current);
    setTrack(null);
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <AudioPlayerContext.Provider value={{ track, playing, progress, duration, currentTime, play, pause, resume, seek, close }}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  return useContext(AudioPlayerContext);
}
